export interface ErrorLogEntry {
  id: string
  timestamp: string
  action: string
  message: string
  payload?: any
}

type Listener = () => void

class ErrorLogService {
  private entries: ErrorLogEntry[] = []
  private listeners: Set<Listener> = new Set()

  log(action: string, message: string, payload?: any) {
    const entry: ErrorLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      action,
      message,
      payload,
    }
    this.entries = [entry, ...this.entries]
    this.notify()
    // Also log to console for debugging
    console.error(`[ErrorLog] ${action}:`, message, payload)
  }

  getAll(): ErrorLogEntry[] {
    return this.entries
  }

  clear() {
    this.entries = []
    this.notify()
  }

  get count(): number {
    return this.entries.length
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify() {
    this.listeners.forEach((fn) => fn())
  }
}

// Singleton
export const errorLogService = new ErrorLogService()

// React hook
import { useState, useEffect } from 'react'

export function useErrorLog() {
  const [entries, setEntries] = useState<ErrorLogEntry[]>(
    errorLogService.getAll(),
  )

  useEffect(() => {
    const unsubscribe = errorLogService.subscribe(() => {
      setEntries([...errorLogService.getAll()])
    })
    return unsubscribe
  }, [])

  return {
    entries,
    count: entries.length,
    clear: () => errorLogService.clear(),
    log: (action: string, message: string, payload?: any) =>
      errorLogService.log(action, message, payload),
  }
}
